import React, { useState } from 'react';
import { Form, Input, Switch, Button, Card, message, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { makePostCall } from '../../utils/helper';
import { getUserDetails } from '../../utils/common';
import './add-center.scss';

const AddCenter = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await makePostCall('/centres', {
        ...values,
        active: values.active !== undefined ? values.active : true,
      });

      if (response.data.success) {
        message.success('Center created successfully');
        form.resetFields();
        // Optionally navigate to centers list
        // navigate('/settings', { state: { activeKey: 'centers_list' } });
      } else {
        throw new Error(response.data.message || 'Failed to create center');
      }
    } catch (error) {
      message.error(error.message || 'Failed to create center');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  return (
    <div className="admin-container add-center-container">
      <div className="admin-header">
        <h2>Add New Center</h2>
      </div>

      <Card className="add-center-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="admin-form add-center-form"
          initialValues={{
            active: true,
          }}
        >
          <div className="form-grid">
            <Form.Item
              label="Center Name"
              name="name"
              rules={[
                { required: true, message: 'Please enter center name' },
                { max: 100, message: 'Name must be less than 100 characters' },
              ]}
            >
              <Input placeholder="Enter center name (e.g., City General Hospital)" size="large" />
            </Form.Item>

            <Form.Item
              label="Center Code"
              name="code"
              rules={[
                { required: true, message: 'Please enter center code' },
                { max: 20, message: 'Code must be less than 20 characters' },
              ]}
            >
              <Input placeholder="Enter center code (e.g., CGH)" size="large" />
            </Form.Item>

            <Form.Item
              label="IP Address"
              name="ip"
              rules={[
                { required: true, message: 'Please enter IP address' },
                {
                  pattern: /^(\d{1,3}\.){3}\d{1,3}$/,
                  message: 'Please enter a valid IP address',
                },
              ]}
              tooltip="Enter the IP address of the center's server"
            >
              <Input placeholder="Enter IP address (e.g., 192.168.1.1)" size="large" />
            </Form.Item>

            <Form.Item
              label="Cache Proxy URL"
              name="proxy_url"
              tooltip="Base URL of this centre's cache proxy app, e.g. http://<centre-ip>:3333. Leave empty for centres without a local cache. Used for federated image loading."
              rules={[
                { max: 255, message: 'Proxy URL must be less than 255 characters' },
                {
                  validator: (_, value) => {
                    if (!value?.trim()) return Promise.resolve();
                    try {
                      const url = new URL(value.trim());
                      if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error();
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(new Error('Enter a valid http/https URL, e.g. http://192.168.1.50:3333'));
                    }
                  },
                },
              ]}
            >
              <Input placeholder="e.g. http://192.168.1.50:3333" size="large" />
            </Form.Item>

            <Form.Item
              label="HIS Lab URL"
              name="his_lab_url"
              tooltip="Centre's HIS lab-results portal, e.g. https://his.example.com:5944"
              rules={[
                { max: 255, message: 'HIS Lab URL must be less than 255 characters' },
                {
                  validator: (_, value) => {
                    if (!value?.trim()) return Promise.resolve();
                    try {
                      const url = new URL(value.trim());
                      if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error();
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(new Error('Enter a valid http/https URL'));
                    }
                  },
                },
              ]}
            >
              <Input placeholder="e.g. https://his.example.com:5944" size="large" />
            </Form.Item>

            <Form.Item
              label="Location"
              name="location"
              rules={[
                { max: 200, message: 'Location must be less than 200 characters' },
              ]}
            >
              <Input placeholder="Enter location (e.g., Downtown)" size="large" />
            </Form.Item>

            <Form.Item
              label="Contact Email"
              name="contact"
              rules={[
                { max: 100, message: 'Contact must be less than 100 characters' },
                { type: 'email', message: 'Please enter a valid email address' },
              ]}
            >
              <Input placeholder="Enter contact email" size="large" />
            </Form.Item>

            <Form.Item
              label="Active Status"
              name="active"
              valuePropName="checked"
              tooltip="Toggle to set the center as active or inactive"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
            </Form.Item>
          </div>

          <Form.Item className="form-actions">
            <Space size="middle">
              <Button size="large" onClick={handleReset}>
                Reset
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} size="large">
                Create Center
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AddCenter;
